import { useEffect, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { useParams } from "react-router-dom";

import { MarkdownEditor } from "~/common/components/ui";
import { ConsultationInputLayout } from "~/providers/components";
import {
  useCompleteReimbursementReport,
  useGetPatient,
  useUpdateReimbursementReport,
} from "~/providers/hooks";
import {
  getReimbursementReportValues,
  PatientParams,
  reimbursementReportSchema,
  ReimbursementReportSchemaType,
} from "~/providers/utils";
import { ConsultationReimbursementReport } from "~/providers/utils/types/consultation";
import { DisabledReimbursementReportMessage } from "./DisabledReimbursementReportMessage";
import { ReimbursementReportCompleteSaveButtons } from "./ReimbursementReportCompleteSaveButtons";
import { ReimbursementReportConfirmationModal } from "./ReimbursementReportConfirmationModal";

interface ReimbursementReportFormProps {
  data?: ConsultationReimbursementReport | null;
}

export const ReimbursementReportForm = ({
  data,
}: ReimbursementReportFormProps) => {
  const [saveChangesAutomatically, setSaveChangesAutomatically] =
    useState(true);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);

  const { patientId } = useParams<keyof PatientParams>() as PatientParams;
  const { data: patient } = useGetPatient(patientId);
  const isFormDisabled = !data;
  const consultationId = patient?.consultation?.id;

  const toggleConfirmationModal = () =>
    setShowConfirmationModal((prev) => !prev);

  const { updateReimbursementReportMutation, isPending: pendingUpdateData } =
    useUpdateReimbursementReport({
      consultationId,
      showSuccessMessage: !saveChangesAutomatically,
    });

  const { completeReimbursementReportMutation, isPending: pendingComplete } =
    useCompleteReimbursementReport(consultationId);

  const {
    control,
    handleSubmit,
    formState: { errors, isDirty },
    getValues,
    setValue,
  } = useForm<ReimbursementReportSchemaType>({
    resolver: zodResolver(reimbursementReportSchema),
    values: {
      saveData: true,
      data: {
        ...getReimbursementReportValues(data),
      },
    },
  });

  const onSubmit: SubmitHandler<ReimbursementReportSchemaType> = (data) => {
    if (data.saveData) {
      setSaveChangesAutomatically(false);
      updateReimbursementReportMutation(data.data);
    } else {
      toggleConfirmationModal();
    }
  };

  const handleSubmitReimbursementReportCompletion = () => {
    const reimbursementReportData = getValues("data");
    completeReimbursementReportMutation(reimbursementReportData);
  };

  useEffect(() => {
    return () => {
      if (!isDirty || !saveChangesAutomatically) return;
      const formValues = getValues();
      !isFormDisabled &&
        formValues.saveData &&
        updateReimbursementReportMutation(formValues.data);
    };
  }, [isDirty, saveChangesAutomatically]);

  return (
    <div className="flex size-full flex-col gap-4">
      {isFormDisabled && <DisabledReimbursementReportMessage />}
      <form
        className="flex size-full flex-col gap-6"
        onSubmit={handleSubmit(onSubmit)}
      >
        <ConsultationInputLayout label="Progress note">
          <Controller
            control={control}
            name="data.final_note"
            render={({ field }) => (
              <MarkdownEditor
                value={field.value ?? ""}
                onChange={field.onChange}
                onBlur={field.onBlur}
                disabled={isFormDisabled}
                error={errors.data?.final_note?.message}
              />
            )}
          />
        </ConsultationInputLayout>
        <ReimbursementReportCompleteSaveButtons
          isFormDisabled={isFormDisabled}
          pendingUpdateData={pendingUpdateData}
          setValue={setValue}
          isDirty={isDirty}
        />
      </form>
      <ReimbursementReportConfirmationModal
        isPending={pendingComplete}
        show={showConfirmationModal}
        onClose={toggleConfirmationModal}
        onSubmit={handleSubmitReimbursementReportCompletion}
      />
    </div>
  );
};
